<template>
  <b-modal
    :id="MODAL_ID"
    title="Animation settings"
    size="lg"
    ok-title="OK"
    cancel-title="Cancel"
    @ok="handleSave"
    @cancel="handleCancel"
  >
    <div class="layout-animation-settings-modal">
      <div
        v-if="!frames.length"
        class="layout-animation-settings-modal__empty"
      >
        No frames available
      </div>

      <div
        v-else
        class="layout-animation-settings-modal__grid"
      >
        <div
          v-for="(frame, index) in frames"
          :key="frame.id"
          class="layout-animation-settings-modal__card"
          :class="{
            'layout-animation-settings-modal__card--dragging': draggedIndex === index,
            'layout-animation-settings-modal__card--drop-before': dragOverIndex === index && dragOverPosition === 'before' && draggedIndex !== index,
            'layout-animation-settings-modal__card--drop-after': dragOverIndex === index && dragOverPosition === 'after' && draggedIndex !== index,
          }"
          draggable="true"
          @dragstart="onDragStart(index, $event)"
          @dragover="onDragOver(index, $event)"
          @dragleave="onDragLeave(index)"
          @drop="onDrop(index)"
          @dragend="onDragEnd"
        >
          <div class="layout-animation-settings-modal__preview">
            <img
              v-if="frame.previewUrl"
              :src="frame.previewUrl"
              class="layout-animation-settings-modal__preview-image"
              :alt="`Frame ${index + 1} preview`"
            >
            <span
              v-else
              class="layout-animation-settings-modal__preview-placeholder"
            >
              No preview
            </span>

            <span class="layout-animation-settings-modal__order-badge">{{ index + 1 }}</span>
          </div> 

          <div class="layout-animation-settings-modal__meta">
            <div class="layout-animation-settings-modal__title">
              Frame {{ String(index + 1).padStart(2, '0') }}
            </div>
            <div class="layout-animation-settings-modal__template-name">
              {{ frame.templateName }}
            </div>

            <div class="layout-animation-settings-modal__info-row">
              <div
                v-if="frame.width && frame.height"
                class="layout-animation-settings-modal__size"
              >
                {{ frame.width }} × {{ frame.height }}
              </div>

              <div class="layout-animation-settings-modal__duration-row">
                <i class="layout-animation-settings-modal__duration-icon icon-clock" />
                <b-form-input
                  v-model.number="frame.durationMs"
                  type="number"
                  size="sm"
                  :min="MIN_DURATION"
                  :max="MAX_DURATION"
                  :step="STEP"
                  class="layout-animation-settings-modal__duration-input"
                  @click.stop
                />
                <span class="layout-animation-settings-modal__duration-unit">ms</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #modal-footer="{ ok, cancel }">
      <div class="layout-animation-settings-modal__footer">
        <span class="layout-animation-settings-modal__selected-label">
          {{ frames.length }} frame{{ frames.length === 1 ? '' : 's' }}
        </span>
        <div class="layout-animation-settings-modal__footer-actions">
          <b-button
            variant="light"
            @click="cancel()"
          >
            Cancel
          </b-button>
          <b-button
            variant="success"
            @click="ok()"
          >
            Save
          </b-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {LAYOUT_ANIMATION_EVENTS, layoutAnimationEventBroker} from "@frontend/group/modules/layout-animation/event-broker";
import {ShowPageScopeCollector} from "@frontend/group/modules/scope-collectors/show-page-collector";
import {editorControlEventBroker, EVENTS} from "@frontend/group/modules/editor-control-popup/event-broker";
import {calculateTimeByTotalDuration, getAnimationData} from "@frontend/services/EditorControls/animation-module/utils";

const MODAL_ID = 'layout-animation-settings-modal';

const MIN_DURATION = 0;
const MAX_DURATION = 50000;
const DEFAULT_DURATION = 3; // Seconds
const STEP = 50;

const PREVIEW_MAX_WIDTH = 300;

const DEFAULT_ANIMATION = {
  totalDuration: DEFAULT_DURATION,
  order: null,
}

export default {
  name: 'LayoutAnimationSettingsModal',

  data: () => ({
    MODAL_ID,
    MIN_DURATION,
    MAX_DURATION,
    STEP,
    frames: [],
    initialFramesSnapshot: '',
    draggedIndex: null,
    dragOverIndex: null,
    dragOverPosition: null,
  }),

  mounted() {
    layoutAnimationEventBroker.on(LAYOUT_ANIMATION_EVENTS.INIT_SETTINGS_MODAL, this.init.bind(this));
  },

  methods: {
    init() {
      const previews = window.previews;

      if (!previews || !previews.length) {
        console.error('window.previews is empty or not defined');
        return;
      }

      this.frames = previews
        .map((preview, index) => {
          const animation = this.getAnimation(preview);

          return {
            id: preview.data?.id ?? `frame-${index}`,
            raw: preview,
            previewUrl: this.getPreviewUrl(preview),
            templateName: preview.data?.template_name ?? preview.template.name ?? '',
            durationMs: animation.totalDuration,
            order: animation.order ?? index,
            width: preview.data?.template_width ?? null,
            height: preview.data?.template_height ?? null,
          };
        })
        .sort((a, b) => a.order - b.order);
      
      this.initialFramesSnapshot = this.getFramesSnapshot(this.frames);
      this.$bvModal.show(MODAL_ID);
    },

    getFramesSnapshot(frames) {
      return JSON.stringify(frames.map(f => ({
        id: f.id,
        durationMs: f.durationMs,
      })));
    },

    getPreviewUrl(preview) {
      try {
        const canvas = preview.canvas;
        const multiplier = this.getDownscaleMultiplier(canvas);

        return canvas.toDataURL({
          format: 'png',
          multiplier,
        });
      } catch (e) {
        console.error('Failed to generate preview from canvas', e);
        return '';
      }
    },

    getDownscaleMultiplier(canvas) {
      const width = canvas.getWidth?.() ?? canvas.width;
      if (!width) return 1;

      return Math.min(1, PREVIEW_MAX_WIDTH / width);
    },

    onDragStart(index, event) {
      this.draggedIndex = index;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', String(index));
    },

    onDragOver(index, event) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';

      const rect = event.currentTarget.getBoundingClientRect();
      const isAfter = (event.clientX - rect.left) > rect.width / 2;

      this.dragOverIndex = index;
      this.dragOverPosition = isAfter ? 'after' : 'before';
    },

    onDragLeave(index) {
      if (this.dragOverIndex === index) {
        this.dragOverIndex = null;
        this.dragOverPosition = null;
      }
    },

    onDrop(index) {
      if (this.draggedIndex === null) {
        return;
      }

      let targetIndex = this.dragOverPosition === 'after' ? index + 1 : index;

      if (this.draggedIndex < targetIndex) {
        targetIndex -= 1;
      }

      if (targetIndex === this.draggedIndex) {
        this.draggedIndex = null;
        this.dragOverIndex = null;
        this.dragOverPosition = null;
        return;
      }

      const frames = [...this.frames];
      const [moved] = frames.splice(this.draggedIndex, 1);
      frames.splice(targetIndex, 0, moved);

      this.frames = frames;
      this.draggedIndex = null;
      this.dragOverIndex = null;
      this.dragOverPosition = null;
    },

    onDragEnd() {
      this.draggedIndex = null;
      this.dragOverIndex = null;
      this.dragOverPosition = null;
    },

    async handleSave() {
      this.frames.forEach((frame, index) => {
        const newTotalDuration = parseFloat((frame.durationMs / 1000).toFixed(3));
        
        const prevAnimationData = getAnimationData(frame.raw.canvas, frame.raw.data.animation);
        const newNodes = calculateTimeByTotalDuration(prevAnimationData.nodes, newTotalDuration);

        editorControlEventBroker.fire(EVENTS.ANIMATION_MODULE_CHANGED, {
          ...prevAnimationData,
          nodes: newNodes,
          order: index,
          totalDuration: newTotalDuration,
          instanceId: frame.raw.id
        })
      });
    },

    handleCancel() {
      this.$bvModal.hide(MODAL_ID);
    },

    getAnimation(preview) {
      let animation = preview?.data?.animation;

      if (!animation) {
        return { ...DEFAULT_ANIMATION };
      }

      if (typeof animation === 'string') {
        try {
          animation = JSON.parse(animation);
        } catch (e) {
          console.error('Invalid animation JSON:', animation);
          return { ...DEFAULT_ANIMATION };
        }
      }

      const durationSec = Number(animation.totalDuration ?? DEFAULT_ANIMATION.totalDuration);

      return {
        totalDuration: Math.round(durationSec * 1000),
        order: animation.order,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.layout-animation-settings-modal {
  &__empty {
    padding: 40px 0;
    text-align: center;
    color: #9ca3af;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  &__card {
    position: relative;
    border: 2px solid #e5e7eb;
    border-radius: 10px;
    padding: 10px;
    cursor: grab;
    background: #fff;
    transition: border-color 0.15s, box-shadow 0.15s, opacity 0.15s;

    &:hover {
      border-color: #1a9c5f;
      box-shadow: 0 0 0 2px rgba(26, 156, 95, 0.2);
    }

    &:active {
      cursor: grabbing;
    }

    &--dragging {
      opacity: 0.4;
    }

    &--drop-before::before,
    &--drop-after::after {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      width: 3px;
      background: #1a9c5f;
      border-radius: 2px;
    }

    &--drop-before::before {
      left: -10px;
    }

    &--drop-after::after {
      right: -10px;
    }
  }

  &__preview {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 140px;
    border-radius: 6px;
    background-image: linear-gradient(45deg, #f0f0f0 25%, transparent 25%),
    linear-gradient(-45deg, #f0f0f0 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #f0f0f0 75%),
    linear-gradient(-45deg, transparent 75%, #f0f0f0 75%);
    background-size: 16px 16px;
    background-position: 0 0, 0 8px, 8px -8px, -8px 0;
    background-color: #fafafa;
    overflow: hidden;
    user-select: none;
  }

  &__preview-image {
    max-width: 100%;
    max-height: 100%;
    pointer-events: none;
  }

  &__preview-placeholder {
    color: #9ca3af;
    font-size: 13px;
  }

  &__order-badge {
    position: absolute;
    top: 6px;
    left: 6px;
    min-width: 20px;
    height: 20px;
    padding: 0 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #1a9c5f;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    border-radius: 10px;
  }

  &__meta {
    margin-top: 8px;
  }

  &__title {
    font-size: 13px;
    font-weight: 700;
    color: #1f2937;
  }
  
  &__template-name {
    font-size: 12px;
    color: #6b7280;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
    height: calc(1.4em * 2);
    line-height: 1.4em;
  }

  &__info-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 6px;
  }

  &__size {
    font-size: 12px;
    color: #9ca3af;
  }

  &__duration-row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-left: auto;
  }

  &__duration-icon {
    font-size: 12px;
    color: #6b7280;
  }

  &__duration-input {
    max-width: 76px;
    cursor: text;
  }

  &__duration-unit {
    font-size: 12px;
    color: #6b7280;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  &__selected-label {
    font-size: 13px;
    color: #6b7280;
  }

  &__footer-actions {
    display: flex;
    gap: 8px;
  }
}
</style>
